import type { EraName } from './types';

export interface EraDef {
  name: EraName;
  /** 元年に対応する西暦。 */
  startYear: number;
  /** その元号に実在する最大の年。令和は開いているため null。 */
  maxYear: number | null;
}

export const ERAS: Record<EraName, EraDef> = {
  明治: { name: '明治', startYear: 1868, maxYear: 45 },
  大正: { name: '大正', startYear: 1912, maxYear: 15 },
  昭和: { name: '昭和', startYear: 1926, maxYear: 64 },
  平成: { name: '平成', startYear: 1989, maxYear: 31 },
  令和: { name: '令和', startYear: 2019, maxYear: null },
};

/** 略記を含む元号表記の正規化テーブル。 */
export const ERA_ALIASES: Record<string, EraName> = {
  明治: '明治',
  明: '明治',
  M: '明治',
  m: '明治',
  大正: '大正',
  大: '大正',
  T: '大正',
  t: '大正',
  昭和: '昭和',
  昭: '昭和',
  S: '昭和',
  s: '昭和',
  平成: '平成',
  平: '平成',
  H: '平成',
  h: '平成',
  令和: '令和',
  令: '令和',
  R: '令和',
  r: '令和',
};

/** 1 つの西暦年に 2 つの元号が含まれる年。月日がなければ確定できない。 */
export const AMBIGUOUS_WESTERN_YEARS = new Set([1912, 1926, 1989, 2019]);

export const MIN_WESTERN_YEAR = 1868;
export const MAX_WESTERN_YEAR = 2100;

export const MAX_INPUT_LENGTH = 20000;

/**
 * 事件符号として許容する文字。
 * ひらがな・カタカナ + 実際に事件符号で使われる限られた漢字のみ。
 * 「法律」「政令」などを事件符号と誤認しないため、漢字は必ずホワイトリスト運用にする。
 */
export const SYMBOL_CHAR_CLASS =
  '\\u3041-\\u3096\\u30A1-\\u30FA行家少特合刑医交受許分労記選破執保再更財共秘';

/**
 * 括弧なしでも事件符号と認めるもの（例: `R5受2207` / `令4行ヒ283`）。
 * ひらがなは助詞と衝突するため（「平成26年に1000人」など）括弧なしでは認めない。
 */
export const BARE_SYMBOLS: string[] = [
  // 複数文字（長いものから照合する）
  '行ツ',
  '行ヒ',
  '行フ',
  '行コ',
  '行ス',
  '行ケ',
  '行ウ',
  '行ク',
  '行タ',
  '行ソ',
  '行ノ',
  '家イ',
  '家ロ',
  '家ハ',
  '家ニ',
  '家ホ',
  '家ヘ',
  '少調',
  '少ハ',
  '特わ',
  '合わ',
  '刑わ',
  '医わ',
  '交わ',
  // 漢字 1 文字
  '受',
  '許',
  '家',
  '少',
  '分',
  '労',
  // カタカナ 1 文字
  'ア',
  'イ',
  'ウ',
  'エ',
  'オ',
  'カ',
  'キ',
  'ク',
  'ケ',
  'コ',
  'サ',
  'シ',
  'ス',
  'セ',
  'ソ',
  'タ',
  'チ',
  'ツ',
  'テ',
  'ト',
  'ナ',
  'ニ',
  'ヌ',
  'ネ',
  'ノ',
  'ハ',
  'ヒ',
  'フ',
  'ヘ',
  'ホ',
  'マ',
  'ミ',
  'ム',
  'メ',
  'モ',
  'ヤ',
  'ユ',
  'ヨ',
  'ラ',
  'リ',
  'ル',
  'レ',
  'ロ',
  'ワ',
  'ヰ',
  'ヲ',
];

/**
 * 参考用の事件符号リスト。ここに無くても構造が完全なら出力する（warning を付けるだけ）。
 * 本ツールは表記統一が目的であり、事件符号の法的分類を判定するものではない。
 */
export const KNOWN_CASE_SYMBOLS = new Set<string>([
  ...BARE_SYMBOLS,
  // 最高裁
  'オ',
  '受',
  'あ',
  'つ',
  'く',
  'し',
  'す',
  'ラ',
  'ク',
  '行ツ',
  '行ヒ',
  '行フ',
  '許',
  '分',
  'テ',
  'ヤ',
  '記',
  // 高裁
  'ネ',
  'う',
  'ツ',
  'ウ',
  'ケ',
  'ノ',
  '行コ',
  '行ス',
  '行ケ',
  '行タ',
  '行ソ',
  '労',
  // 地裁
  'ワ',
  'ヲ',
  'ヨ',
  'モ',
  'レ',
  'ル',
  'リ',
  'ヌ',
  'ナ',
  'フ',
  'ヒ',
  'ホ',
  'ヘ',
  'ト',
  'チ',
  'カ',
  'キ',
  'コ',
  'サ',
  'シ',
  'セ',
  'ソ',
  'タ',
  'ミ',
  'ム',
  'メ',
  'ユ',
  'ロ',
  'ヰ',
  'わ',
  '特わ',
  '合わ',
  '刑わ',
  '医わ',
  '交わ',
  '行ウ',
  '行ク',
  // 簡裁
  'ハ',
  'ロ',
  'い',
  'ろ',
  'は',
  // 家裁
  '家',
  '家イ',
  '家ロ',
  '家ハ',
  '家ニ',
  '家ホ',
  '家ヘ',
  '少',
  '少調',
  '少ハ',
]);

/**
 * 通し番号の直後にこれらが続く場合、事件番号ではない（金額・人数・期間など）。
 * 「号」は事件番号の一部なので含めない。
 */
export const TRAILING_UNIT_BLOCKLIST = new Set([
  '円',
  '人',
  '件',
  '日',
  '月',
  '年',
  '条',
  '項',
  '万',
  '億',
  '個',
  '回',
  '名',
  '台',
  '％',
  '%',
  '度',
  '倍',
  '割',
  '歳',
  '票',
  '株',
  '通',
  '頁',
  '％',
]);

/** 省略表記が直前の記載を引き継いでいると判断できる接続表現。 */
export const CONNECTORS = [
  '及び',
  'および',
  '並びに',
  'ならびに',
  'ないし',
  '乃至',
  'かつ',
  '、',
  '，',
  ',',
  '・',
  '～',
  '〜',
  '−',
  '及',
  'と',
  'や',
  'から',
];

/** 文脈スコープを区切る文字（同一文・同一段落を超えて推測しないため）。 */
export const SCOPE_BREAK_CHARS = new Set(['。', '．', '！', '？', '!', '?', '；', ';', '\n', '\r']);
